<script lang="ts">
  import { page } from '$app/stores';
  import Button from '$components/Button.svelte';
  import { preferencesDialog } from '$components/PreferencesModal.svelte';
  import type { LayoutConfig } from '$types/races';
  import { Bookmaker } from '$utils/bookmakers';
  import TablerPlus from '~icons/tabler/plus';
  import AddColumnDropdown from './AddColumnDropdown.svelte';
  import LayoutNameInput from './LayoutNameInput.svelte';
  import LayoutSelectionDropdown from './LayoutSelectionDropdown.svelte';
  import RunnerSortDirectionDropdown from './RunnerSortDirectionDropdown.svelte';
  import ShowRemainingBookmakersDropdown from './ShowRemainingBookmakersDropdown.svelte';
  import SortablePreferenceList from './SortablePreferenceList.svelte';

  function createNewCustomLayout({ name = '', switchToLayout = false } = {}) {
    // if the name is not supplied, find the next available name
    if (!name) {
      const existingNames = layoutConfigStore
        ?.getAllNonDefault()
        .map((l) => l.name);
      const defaultName = 'Custom Layout';
      let i = 1;
      while (existingNames?.includes(`${defaultName} ${i}`)) {
        i++;
      }
      name = `${defaultName} ${i}`;
    }

    layoutConfigStore?.addOne({
      name,
      scratchedRunnerVisibility: 'BOTTOM',
      showRemainingBookmakers: 'FIXED_WIN',
      remainingBookmakerSort: 'MARKET_FRAME',
      boldTopPrice: false,
      runnerSort: 'BARRIER',
      runnerPriceSortBookmaker: 'Betfair',
      layoutPreferences: [
        {
          bookmaker: Bookmaker.BETFAIR,
          side: 'BACK',
          level: 'ZERO',
          locked: true,
          type: 'FIXED_WIN'
        },
        {
          bookmaker: Bookmaker.BETFAIR,
          side: 'LAY',
          level: 'ZERO',
          locked: true,
          type: 'FIXED_WIN'
        },
        {
          type: 'TEXT',
          size: 'SMALL',
          name: 'Custom 1'
        }
      ]
    } as LayoutConfig);
    if (switchToLayout) layoutConfigStore?.activeLayoutConfigName.set(name);
  }

  $: ({ session, layoutConfigStore } = $page.data);
  $: activeLayoutConfigName = layoutConfigStore?.activeLayoutConfigName;
  $: {
    if ($preferencesDialog.expanded) {
      // switch to a non-default layout when opening preferences
      if (layoutConfigStore?.getAllNonDefault().length === 0) {
        // user has no custom layouts
        createNewCustomLayout({ switchToLayout: true });
      } else if (
        $activeLayoutConfigName === 'Default Win' ||
        $activeLayoutConfigName === 'Default Place'
      ) {
        layoutConfigStore?.activeLayoutConfigName.set(
          layoutConfigStore.getAllNonDefault()[0].name
        );
      }
    }
  }

  // monitor for if the user has deleted the last custom layout and close the dialog
  $: {
    if (layoutConfigStore?.getAllNonDefault().length === 0) {
      preferencesDialog.close();
    }
  }
</script>

<div class="relative flex flex-row items-center gap-x-2">
  <!-- <h3 class="flex-1 text-lg font-medium leading-6 text-gray-900">
    Customise Layouts
  </h3> -->

  <div class="flex-1">
    <h2 class="text-base font-semibold leading-7 text-gray-900">
      Customise Layouts
    </h2>
    <p class="mt-1 max-w-2xl text-sm leading-6 text-gray-600">
      Create and customise your own layouts. Layouts will sync across devices.
    </p>
  </div>
  <AddColumnDropdown />
</div>
<div class="mt-2 flex flex-1 flex-row gap-x-6">
  <div class="relative hidden flex-1 flex-col gap-y-6 md:flex">
    <Button
      color="slate"
      rounded="rounded-md"
      class="flex w-1/3 self-end"
      size="small"
      on:click={() => createNewCustomLayout({ switchToLayout: true })}
      ><TablerPlus class="mr-1" />New Layout</Button
    >
    <LayoutSelectionDropdown />
    <LayoutNameInput />
    <ShowRemainingBookmakersDropdown />
    <RunnerSortDirectionDropdown />
    <Button
      variant="outline"
      color="red"
      size="small"
      class="w-1/2"
      on:click={() => {
        if (!layoutConfigStore || !$activeLayoutConfigName) {
          console.error('no layout config store');
          return;
        }
        if (
          confirm(
            `Are you sure you want to delete the ${$activeLayoutConfigName} layout?`
          )
        ) {
          // if that is the last custom layout, close the dialog
          const otherLayouts = layoutConfigStore.getAllNonDefault();
          if (otherLayouts.length === 1) {
            preferencesDialog.close();
          }
          layoutConfigStore.removeOne($activeLayoutConfigName);
          layoutConfigStore.activeLayoutConfigName.set(
            layoutConfigStore.getAllNonDefault()?.[0]?.name ?? 'Default Win'
          );
        }
      }}>Delete Layout</Button
    >
  </div>
  <div class="flex flex-1 flex-col">
    <div class="flex-1 overflow-y-scroll bg-slate-50 shadow sm:rounded-md">
      <SortablePreferenceList />
    </div>

    <div class="mt-4 flex flex-col justify-end">
      <div class="mb-3 mt-3">
        <div class="mb-1 border-b pb-1">
          <span class="text-xs">
            Locking a column will prevent it from being moved when sorting.
          </span>
        </div>
        <span class="text-xs">
          Any bookmakers that are not explicitly added to this list can be added
          with the <b>Show Remaining Bookmakers</b> option on the left.
        </span>
      </div>
      <Button on:click={preferencesDialog.close}>Save</Button>
    </div>
  </div>
</div>
